import classNames from "classnames";
import { Button, ButtonProps } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { CaretDown } from "components/icons/CaretDown";
import {
  DOWNLOAD_FORMAT,
  DOWNLOAD_FORMAT_TYPES,
  EXPORT_FORMAT,
  ExportFormatType,
  GENERAL_LEDGER,
  REPORTS_LABELS,
} from "constants/bookkeeping";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useWindowSize } from "hooks/useWindowSize";
import { useAuth } from "hooks/useAuth";
import { useQuery } from "hooks/useQuery";
import { useSelector } from "react-redux";
import { useExportLedgerMutation } from "store/apis/generalLedger";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import { RootState } from "store/store";
import { BackendError } from "types/utils/error";
import { openLink } from "utils/openLink";
import { ExportOptions } from "components/ExportOptions/ExportOptions";
import { SelectFolderForUpload } from "components/SelectFolderForUpload";
import { useModal } from "hooks/useModal";
import { useState } from "react";
import { SavedToInkleDocsModal } from "components/SavedToInkleDocsModal/SavedToInkleDocsModal";
import { ReportType } from "./GeneralLedgerFilters";
import { PERIOD } from "constants/revenueMetrics";
import ConditionalToolTip from "components/design/conditionalToolTip";

const ExpandCaret = ({ isOpen }: { isOpen: boolean }) => (
  <span
    className={classNames("t-flex t-transition-transform", {
      "t-rotate-180": isOpen,
    })}
  >
    <CaretDown />
  </span>
);

export const ExportLedger = ({
  reportType: reportTypeProp = GENERAL_LEDGER,
  size = "regular",
}: {
  reportType?: ReportType;
  size?: ButtonProps["size"];
} = {}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();
  const { email } = useAuth();
  const query = useQuery();
  const { isMobile } = useWindowSize();
  const [getPreviewUrl] = useLazyGetPreviewUrlQuery();

  const [exportGeneralLedger, { isLoading: isExporting }] =
    useExportLedgerMutation();

  const selectFolderModal = useModal();
  const savedToInkleDocsModal = useModal();
  const [selectedFormat, setSelectedFormat] = useState<{
    downloadType: DOWNLOAD_FORMAT_TYPES;
  }>();
  const [savedDocumentId, setSavedDocumentId] = useState<string>();
  const [openFormat, setOpenFormat] = useState<ExportFormatType | null>(null);

  const reportTypeFromQuery = query.get("reportType") as ReportType | null;
  const groupByFromQuery = query.get("groupBy");

  const {
    filters: {
      cashFlow,
      minAmount,
      maxAmount,
      accountingMethod,
      startDate,
      endDate,
      hideZero,
      categoryIds,
    },
    reportType: reportTypeFromStore,
    groupBy: groupByFromStore,
  } = useSelector((state: RootState) => state.ledgerFilter);

  const reportType =
    reportTypeProp || reportTypeFromQuery || reportTypeFromStore;
  const groupBy = groupByFromQuery || groupByFromStore;

  const filterValues = {
    cashFlow,
    minAmount: ((minAmount.value || "") as string)
      ?.replaceAll("$", "")
      .replaceAll(",", ""),
    maxAmount: ((maxAmount.value || "") as string)
      ?.replaceAll("$", "")
      .replaceAll(",", ""),
    accountingMethod,
    startDate,
    endDate,
    hideZero,
    categoryIds,
  };

  const onExport = async (
    downloadType: DOWNLOAD_FORMAT_TYPES,
    exportToMail?: boolean,
    folderId?: string
  ) => {
    try {
      const { general_ledger_document_id } = await exportGeneralLedger({
        groupId,
        entityId,
        ...filterValues,
        reportType,
        comparison_ledger_type: groupBy,
        downloadType,
        export_to_mail: exportToMail,
        folderId,
      }).unwrap();

      if (folderId) {
        setSavedDocumentId(general_ledger_document_id);
        savedToInkleDocsModal.open();
      } else if (!exportToMail) {
        const { download_url } = await getPreviewUrl({
          groupId,
          fileId: general_ledger_document_id,
        }).unwrap();
        openLink(download_url);
        successToast({ message: "Report downloaded" });
      } else {
        successToast({ message: "Report will be sent to your email" });
      }
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const pdfDisabled = groupBy === PERIOD.MONTHLY;

  const toggleFormat = (format: ExportFormatType) => (event: Event) => {
    event.preventDefault();
    setOpenFormat((current) => (current === format ? null : format));
  };

  const isPdfOpen = openFormat === EXPORT_FORMAT.PDF;
  const isExcelOpen = openFormat === EXPORT_FORMAT.EXCEL;

  const pdfOptions = (
    <ExportOptions
      onLocalDownload={() => onExport(DOWNLOAD_FORMAT.DOWNLOAD_PDF, false)}
      onMailExport={() => onExport(DOWNLOAD_FORMAT.DOWNLOAD_PDF, true)}
      onSaveInInkleDocs={() => {
        setSelectedFormat({ downloadType: DOWNLOAD_FORMAT.DOWNLOAD_PDF });
        selectFolderModal.open();
      }}
    />
  );

  const excelOptions = (
    <ExportOptions
      onLocalDownload={() => onExport(DOWNLOAD_FORMAT.DOWNLOAD_EXCEL, false)}
      onMailExport={() => onExport(DOWNLOAD_FORMAT.DOWNLOAD_EXCEL, true)}
      onSaveInInkleDocs={() => {
        setSelectedFormat({ downloadType: DOWNLOAD_FORMAT.DOWNLOAD_EXCEL });
        selectFolderModal.open();
      }}
    />
  );

  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild className="t-group">
        <div>
          <Button
            customType="primary"
            size={size}
            onClick={(e) => e.stopPropagation()}
            isLoading={isExporting}
            disabled={isExporting}
          >
            <div className="t-flex t-items-center t-gap-1.5">
              Export
              <div className="group-data-state-open:t-rotate-180">
                <CaretDown />
              </div>
            </div>
          </Button>
        </div>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content sideOffset={4} align="end" side="bottom">
          {isMobile ? (
            <>
              <ConditionalToolTip
                condition={
                  pdfDisabled && "PDF export is not available for this view"
                }
              >
                <span>
                  <Dropdown.Item
                    disabled={pdfDisabled}
                    onSelect={toggleFormat(EXPORT_FORMAT.PDF)}
                  >
                    <span className="t-flex t-items-center t-justify-between t-w-full">
                      PDF
                      <ExpandCaret isOpen={isPdfOpen} />
                    </span>
                  </Dropdown.Item>
                </span>
              </ConditionalToolTip>
              {isPdfOpen && !pdfDisabled && pdfOptions}
              <Dropdown.Item onSelect={toggleFormat(EXPORT_FORMAT.EXCEL)}>
                <span className="t-flex t-items-center t-justify-between t-w-full">
                  Excel
                  <ExpandCaret isOpen={isExcelOpen} />
                </span>
              </Dropdown.Item>
              {isExcelOpen && excelOptions}
            </>
          ) : (
            <>
              <Dropdown.Sub>
                <ConditionalToolTip
                  condition={
                    pdfDisabled && "PDF export is not available for this view"
                  }
                  align="start"
                  side="left"
                  sideOffset={4}
                >
                  <span>
                    <Dropdown.SubTrigger disabled={pdfDisabled}>
                      PDF
                    </Dropdown.SubTrigger>
                  </span>
                </ConditionalToolTip>
                <Dropdown.Portal>
                  <Dropdown.SubContent sideOffset={6} alignOffset={-5}>
                    {pdfOptions}
                  </Dropdown.SubContent>
                </Dropdown.Portal>
              </Dropdown.Sub>
              <Dropdown.Sub>
                <Dropdown.SubTrigger asChild>Excel</Dropdown.SubTrigger>
                <Dropdown.Portal>
                  <Dropdown.SubContent sideOffset={6} alignOffset={-5}>
                    {excelOptions}
                  </Dropdown.SubContent>
                </Dropdown.Portal>
              </Dropdown.Sub>
            </>
          )}
        </Dropdown.Content>
      </Dropdown.Portal>
      {selectFolderModal.isOpen && (
        <SelectFolderForUpload
          showModal={selectFolderModal.isOpen}
          closeModal={selectFolderModal.close}
          onUpload={(folderId) =>
            onExport(selectedFormat!.downloadType, false, folderId)
          }
        />
      )}
      {savedToInkleDocsModal.isOpen && savedDocumentId && (
        <SavedToInkleDocsModal
          isOpen={savedToInkleDocsModal.isOpen}
          close={savedToInkleDocsModal.close}
          fileId={savedDocumentId}
          reportType={
            reportType === GENERAL_LEDGER
              ? REPORTS_LABELS.GENERAL_LEDGER
              : REPORTS_LABELS.TRIAL_BALANCE
          }
        />
      )}
    </Dropdown.Root>
  );
};
